import React, { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import IdleModal from './IdleModal'
import { escapeHTML } from '../../../utils/escapeHTML'
import LanguageStrings from '../../../i18n/locales'
import { getLicenseText, openFileInNewTab } from '../services/getFileService'
import Modal from '../../../components/Modal'
import { GenericError } from '../../../components/ErrorComponents/GenericError'
import { useNav } from '../../../App'
import GlobalMenu from './GlobalMenu'
import { CircularProgress, Tooltip, AppBar, Toolbar, IconButton, Box, List, ListItem, ListItemButton, ListItemIcon, Drawer } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import UserProfile from './UserProfile'
import { iconMap } from '../../../utils/AppUtil'
import honeywellLogo from '../../../assets/Honeywell-logo.svg'

const dashboardStrings = LanguageStrings().Dashboard

const DashboardLayout = (props: any): any => {
  const [showLicense, setShowLicense] = useState(false)
  const [license, setLicense] = useState<'loading' | 'error' | string | undefined>('loading')
  const auth = useAuth()
  const { showSidebar, menuItems, lastActiveItemId, setLastActiveItemId } = useNav()
  const [globalMenuOpen, setGlobalMenuOpen] = React.useState(false)

  // istanbul ignore next
  const serviceVersion: string = window._env_?.REACT_APP_SERVICE_VERSION !== undefined ? window._env_.REACT_APP_SERVICE_VERSION : ''

  // istanbul ignore next
  const onGlobalMenuClick = (): any => {
    setGlobalMenuOpen(true)
  }

  const onLogout = (): void => {
    // istanbul ignore next
    auth.signoutRedirect({
      post_logout_redirect_uri: escapeHTML(window.location.origin) + '/logout'
    })
      .catch((e) => {
        // istanbul ignore next
        console.log(e)
      })
    auth.removeUser()
      .then(() => {
        console.log('User removed from storage')
      }).catch((e) => {
        console.log('Error while removing user from storage', e)
      })
  }
  const navigate = useNavigate()

  const handleHelpClick = (): void => {
    document.body.style.cursor = 'wait'
    openFileInNewTab('UDP.pdf')
      .catch((e) => {
      // istanbul ignore next
        console.log(e)
      })
  }

  // const shouldShowSearchIcon = (): boolean => {
  //   return false
  // }

  const handleLicenseClick = (): void => {
    setShowLicense(true)
    if (license === 'loading' || license === 'error') {
      setLicense('loading')
      getLicenseText('license.txt')
        .then(response => {
          if (response !== undefined) {
            setLicense(response)
          } else {
            setLicense('error')
          }
        })
        .catch(e => {
          setLicense('error')
        })
    }
  }

  const handleDownloadLicense = (): void => {
    if (license !== undefined) {
      const blob = new Blob([license], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'license.txt'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const getLicenseModal = (): JSX.Element => {
    return (
      <div data-testid='licenseText' style={{ backgroundColor: '#404040', color: '#F0F0F0', overflowY: 'scroll', height: 'calc(80vh -  160px)', marginTop: '20px', fontFamily: 'Honeywell Sans Web' }}>
        <pre
          className="license-text"
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            fontSize: '14px',
            padding: '20px'
          }}
        >
          {license}
        </pre>
      </div>
    )
  }

  const isValidLicene = (): boolean => {
    return license !== undefined && license !== 'loading' && license !== 'error'
  }

  const handleCloseLicense = (): void => {
    setShowLicense(false)
  }

  return (
        <div id='MainLayoutFlexContainer' data-testid='Home'>
          {/* header */}
          <AppBar data-testid='appbar' position='fixed' sx={{ backgroundColor: '#000', boxShadow: 'none', height: '64px' }}>
            <Toolbar data-testid='toolbar' sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton edge="start" color="inherit" aria-label="menu" onClick={onGlobalMenuClick} sx={{ marginLeft: '-21px' }}>
                  <MenuIcon sx={{ width: 24, height: 24, transform: 'scaleY(1.2)' }} />
                </IconButton>
                <IconButton onClick={() => { navigate('/') }}>
                  <Box
                    component='img'
                    src={honeywellLogo}
                    alt='Honeywell logo'
                    sx={{ height: 15 }}
                  />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <UserProfile auth={auth} onLogout={onLogout} />
              </Box>
            </Toolbar>
          </AppBar>
          <GlobalMenu open={globalMenuOpen} onClose={() => { setGlobalMenuOpen(false) }}/>
            <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
              {/* Sidebar Drawer */}
              {showSidebar && menuItems.length > 0 && (
                <Drawer
                  data-testid='sidebar-drawer'
                  variant='permanent'
                  PaperProps={{
                    sx: {
                      width: '44px',
                      color: '#fff',
                      backgroundColor: '#000',
                      display: 'flex',
                      flexDirection: 'column',
                      marginTop: '64px',
                      flexShrink: 0
                    }
                  }}
                >
                  <List sx={{ padding: 0 }}>
                    {menuItems.map((item) => {
                      const Icon = iconMap[item.icon]
                      return (
                        <Tooltip
                          key={item.id}
                          title={item.label}
                          placement='right'
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#fff',
                                color: '#000',
                                padding: '8px',
                                borderRadius: '4px',
                                textTransform: 'capitalize',
                                fontSize: '14px',
                                fontFamily: 'Honeywell Sans Web',
                                lineHeight: '20px'
                              }
                            },
                            arrow: {
                              sx: {
                                color: '#fff'
                              }
                            }
                          }}
                        >
                          <ListItem disablePadding key={item.id}
                            sx={{
                              backgroundColor: lastActiveItemId === item.id ? '#103F61' : 'transparent',
                              transition: 'background-color 0.2s ease',
                              color: lastActiveItemId === item.id ? '#64C3FF' : '#fff',
                              '&:hover': { backgroundColor: lastActiveItemId === item.id ? '#103F61' : '#666565ff' }
                            }}
                          >
                            <ListItemButton
                              key={item.id}
                              aria-label={item.label}
                              onClick={() => {
                                if (item.to !== null && item.to !== undefined && item.to.trim() !== '') {
                                  setLastActiveItemId(item.id)
                                  navigate(item.to)
                                } else if (item.onClick !== null && item.onClick !== undefined) {
                                  item.onClick()
                                } else if (item.id === 'help') {
                                  handleHelpClick()
                                }
                              }}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '12px 0'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 'auto', color: lastActiveItemId === item.id ? '#64C3FF' : '#fff' }}>
                                {Icon !== null && Icon !== undefined ? <Icon /> : null}
                              </ListItemIcon>
                            </ListItemButton>
                          </ListItem>
                        </Tooltip>
                      )
                    })}
                  </List>
                </Drawer>
              )}
              {/* Main Content */}
              <Box component="main" sx={{ flexGrow: 1, ml: showSidebar ? '44px' : 0, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
                <Toolbar />
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box data-testid='dashboard-outlet' className="outlet-content" sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
                    <Outlet />
                  </Box>
                  {/* Footer */}
                  <Box className= 'dashboard-footer' data-testid='dashboard-footer'>
                    <Box className= 'dashboard-footer-left'>
                      <span style={{ cursor: 'default' }}>
                        {`${dashboardStrings.Version} - ${serviceVersion}`}
                      </span>
                      <span><a target='_blank' rel='noopener noreferrer' href='https://www.honeywell.com/us/en/privacy-statement'>
                        {dashboardStrings.Privacy_Policy}
                      </a></span>
                      <span><a target='_blank' rel='noopener noreferrer' href='https://Honpat.com'>
                        {dashboardStrings.Patents}
                      </a></span>
                      <span onClick={handleLicenseClick}>
                        {dashboardStrings.License}
                      </span>
                    </Box>
                    <Box>
                      <span className='copyright'>© 2025 Honeywell International Inc.</span>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            {
              showLicense && <Modal title={dashboardStrings.OSSLicense} width='70vw' height={isValidLicene() ? '80vh' : 'fit-content'} onClose={handleCloseLicense} LabelPositive={isValidLicene() ? dashboardStrings.Download : undefined} onClickPositive={handleDownloadLicense}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {
                    license === 'loading'
                      ? <div className='CircularprogressAmi'>
                        <CircularProgress />
                      </div>
                      : license === 'error'
                        ? <GenericError style={{ height: '300px' }} retry={handleLicenseClick}><p>{dashboardStrings.UnableToLoadLicense}</p></GenericError>
                        : getLicenseModal()
                  }
                </div>
              </Modal>
            }
            <div className='IdleModal'>

                {auth.isAuthenticated
                  // eslint-disable-next-line operator-linebreak
                  ?
                  // istanbul ignore next
                  <IdleModal onLogout={onLogout} />
                  : <></>
                }
            </div>
        </div>
  )
}

export default DashboardLayout
